/**
 * The E1a draft of a year (`lib/at/e1a.ts`): the Kennzahlen to type into FinanzOnline, the
 * Gewinnfreibetrag, and what the Basispauschalierung would give instead. The two choices the books
 * cannot know - the Arbeitsplatzpauschale and the 6 % flat rate - are stored per year on the
 * business, so the draft reads the same tomorrow.
 */
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Stat } from "@/components/shared/stat";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUpdateWorkspace } from "@/hooks/use-workspaces";
import { taxYearOf, type TaxYear, type TaxYears } from "@/lib/api/workspaces";
import {
  computeE1a,
  EXPENSE_CODES,
  REVENUE_CODES,
  workplaceThreshold,
  type E1aCode,
  type E1aInput,
  type Workplace,
} from "@/lib/at/e1a";
import { buildCsv, downloadCsv } from "@/lib/export/tables";
import { useWorkspace } from "@/lib/session/workspace-context";
import { formatMoney } from "@/lib/utils/format";

export function E1aDraft({
  year,
  ...input
}: Omit<E1aInput, "workplace" | "reducedFlatRate"> & { year: number }) {
  const { t } = useTranslation();
  const { workspace, canManage } = useWorkspace();
  const update = useUpdateWorkspace();
  const choices = taxYearOf(workspace, year);
  const draft = computeE1a({ ...input, year, ...choices });
  const hasWorkplace = year >= 2022 && year <= 2026;

  async function choose(change: Partial<TaxYear>) {
    if (!workspace) return;
    const taxYears: TaxYears = {
      ...((workspace.taxYears as TaxYears | null) ?? {}),
      [String(year)]: { ...choices, ...change },
    };
    try {
      await update.mutateAsync({ id: workspace.id, input: { taxYears } });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("e1a.saveFailed"));
    }
  }

  const rows = (codes: E1aCode[]) =>
    codes
      .filter((code) => draft.amounts[code] !== 0)
      .map((code) => (
        <TableRow key={code} data-testid={`e1a-row-${code}`}>
          <TableCell className="ledger-id">{code}</TableCell>
          <TableCell className="text-muted-foreground">{t(`e1a.codes.${code}`)}</TableCell>
          <TableCell className="text-right font-medium num">
            {formatMoney(draft.amounts[code])}
          </TableCell>
        </TableRow>
      ));

  function exportCsv() {
    const lines = [...REVENUE_CODES, ...EXPENSE_CODES, "9221", "9227"] as E1aCode[];
    downloadCsv(
      `e1a-${year}.csv`,
      buildCsv(
        [t("reports.code"), t("reports.description"), t("reports.amount")],
        lines
          .filter((code) => draft.amounts[code] !== 0)
          .map((code) => [
            code,
            t(`e1a.codes.${code}`),
            draft.amounts[code].toFixed(2).replace(".", ","),
          ]),
      ),
    );
  }

  const difference = draft.flat.profit - draft.profit;

  return (
    <div className="space-y-6" data-testid="e1a-draft">
      <div className="grid gap-6 rounded-lg border bg-card p-6 sm:grid-cols-3">
        <Stat label={t("e1a.profitBefore")} value={formatMoney(draft.profitBefore)} />
        <Stat
          label={t("e1a.allowance")}
          value={formatMoney(draft.allowance.basic + draft.allowance.investment)}
          hint={t("e1a.allowanceHint")}
        />
        <Stat
          emphasis
          tone={draft.profit >= 0 ? "brand" : "warning"}
          label={t("e1a.profit")}
          value={formatMoney(draft.profit)}
          testId="e1a-profit"
        />
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-wrap items-end gap-6">
          {hasWorkplace && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground" htmlFor="e1a-workplace">
                {t("e1a.workplace")}
              </Label>
              <Select
                value={choices.workplace}
                onValueChange={(value) => void choose({ workplace: value as Workplace })}
                disabled={!canManage || update.isPending}
              >
                <SelectTrigger id="e1a-workplace" className="w-64" data-testid="e1a-workplace">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(["none", "small", "large"] as const).map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`e1a.workplaces.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={choices.reducedFlatRate}
              onCheckedChange={(value) => void choose({ reducedFlatRate: value === true })}
              disabled={!canManage || update.isPending}
              data-testid="e1a-reduced"
            />
            {t("e1a.reducedFlatRate")}
          </label>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-e1a">
          <Download /> {t("common.export")}
        </Button>
      </div>
      {hasWorkplace && (
        <p className="-mt-3 text-xs text-muted-foreground">
          {t("e1a.workplaceHint", { threshold: formatMoney(workplaceThreshold(year)) })}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border">
        <Table data-testid="e1a-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">{t("reports.code")}</TableHead>
              <TableHead>{t("reports.description")}</TableHead>
              <TableHead className="text-right">{t("reports.amount")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows(REVENUE_CODES)}
            {rows(EXPENSE_CODES)}
            <TableRow className="bg-muted/40">
              <TableCell />
              <TableCell className="font-medium">{t("e1a.profitBefore")}</TableCell>
              <TableCell className="text-right font-semibold num">
                {formatMoney(draft.profitBefore)}
              </TableCell>
            </TableRow>
            {rows(["9221", "9227"])}
          </TableBody>
        </Table>
      </div>
      {draft.allowance.limit > draft.allowance.investment && (
        <p className="text-sm text-muted-foreground" data-testid="e1a-investment-hint">
          {t("e1a.investmentHint", {
            limit: formatMoney(draft.allowance.limit),
            cover: formatMoney(draft.allowance.cover),
          })}
          {year <= 2026 && ` ${t("e1a.securitiesHint")}`}
        </p>
      )}

      <section className="space-y-2 rounded-lg border bg-card p-4" data-testid="e1a-flat">
        <h2 className="font-medium">
          {t("e1a.flatTitle", { rate: String(draft.flat.rate * 100).replace(".", ",") })}
        </h2>
        {draft.flat.allowed ? (
          <>
            <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">{t("e1a.flatExpenses")}</dt>
              <dd className="text-right num">{formatMoney(draft.flat.expenses)}</dd>
              <dt className="text-muted-foreground">{t("e1a.flatExtra")}</dt>
              <dd className="text-right num">{formatMoney(draft.flat.extra)}</dd>
              <dt className="text-muted-foreground">{t("e1a.flatAllowance")}</dt>
              <dd className="text-right num">{formatMoney(draft.flat.allowance)}</dd>
              <dt className="font-medium">{t("e1a.profit")}</dt>
              <dd className="text-right font-semibold num" data-testid="e1a-flat-profit">
                {formatMoney(draft.flat.profit)}
              </dd>
            </dl>
            <p className="text-sm" data-testid="e1a-flat-verdict">
              {difference < 0
                ? t("e1a.flatLower", { amount: formatMoney(-difference) })
                : t("e1a.flatHigher", { amount: formatMoney(difference) })}
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t("e1a.flatNotAllowed")}</p>
        )}
        <p className="text-xs text-muted-foreground">{t("e1a.flatHint")}</p>
      </section>

      <details className="rounded-lg border bg-card p-4 text-sm">
        <summary className="cursor-pointer font-medium">{t("e1a.sourcesTitle")}</summary>
        <ul className="mt-3 space-y-1">
          {draft.sources.map((source, index) => (
            <li key={index} className="flex justify-between gap-4">
              <span className="min-w-0 truncate">
                <span className="mr-2 ledger-id">{source.accountNumber ?? "—"}</span>
                {source.name || t(`e1a.codes.${source.code}`)}
              </span>
              <span className="whitespace-nowrap text-muted-foreground num">
                {source.code} · {formatMoney(source.amount)}
              </span>
            </li>
          ))}
        </ul>
      </details>

      <p className="text-xs text-muted-foreground">{t("e1a.disclaimer")}</p>
    </div>
  );
}
